// Case study content — sourced from LinkedIn/project source data and repository READMEs.

export const projectSlugs = {
  Prefiler: "prefiler",
  AudienceGraph: "audiencegraph",
  "Greenlight AI": "greenlight-ai",
};

export const caseStudyOrder = ["prefiler", "audiencegraph", "greenlight-ai"];

export const caseStudies = {
  prefiler: {
    name: "Prefiler",
    tagline: "Deterministic financial pre-filing review",
    role: "Founder — Prefiler Labs Private Limited",
    status: "Live — India · UAE targeted",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkYXJrJTIwZGFzaGJvYXJkJTIwVUl8ZW58MHx8fHwxNzg5MDgzNDgyfDA&ixlib=rb-4.1.0&q=85",
    overview:
      "Prefiler is a financial/accounting pre-filing review platform built on deterministic financial intelligence. It ingests structured financial data and documents, runs deterministic validations and reconciliation, and produces evidence-linked findings under jurisdiction-aware rules — with controlled, bounded AI and full auditability.",
    sections: [
      {
        heading: "What the platform does",
        list: [
          "Structured financial-data and document ingestion, including structured ledger ingestion",
          "Deterministic validations, reconciliation and evidence checks",
          "Evidence-linked findings tied back to source data",
          "Jurisdiction-aware rules with rule execution and rule/version management",
          "Report generation with auditability",
          "Controlled, bounded AI inside deterministic workflows",
          "API / OEM use cases and jurisdiction packs",
        ],
      },
      {
        heading: "Architecture",
        list: [
          "Google Cloud Platform architecture",
          "Firebase authentication",
          "Cloud Run services",
          "Cloud SQL / PostgreSQL",
          "Google Cloud Storage",
          "Vertex AI abstraction",
        ],
      },
      {
        heading: "Jurisdictions",
        paragraphs: [
          "India validation is live. UAE is planned / targeted through the jurisdiction-pack model, where jurisdiction-aware rules are versioned and executed deterministically.",
        ],
      },
    ],
    stack: ["GCP", "Cloud Run", "Cloud SQL / PostgreSQL", "Firebase Auth", "Cloud Storage", "Vertex AI", "Deterministic Validation", "Ledger Ingestion"],
    links: [
      { label: "prefiler.com", url: "https://prefiler.com" },
      { label: "Validation", url: "https://prefiler.com/validation" },
      { label: "Engine Capabilities", url: "https://prefiler.com/engine-capabilities" },
      { label: "Product Tour", url: "https://prefiler.com/product-tour" },
      { label: "API Contracts", url: "https://prefiler.com/api-contracts" },
      { label: "Jurisdictions", url: "https://prefiler.com/jurisdictions" },
      { label: "Enterprise", url: "https://prefiler.com/enterprise" },
    ],
  },
  audiencegraph: {
    name: "AudienceGraph",
    tagline: "Streaming audience operations investigation",
    role: "Design & Engineering — public repository",
    status: "Live on Cloud Run",
    image:
      "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYXJrJTIwZGFzaGJvYXJkJTIwVUl8ZW58MHx8fHwxNzg5MDgzNDgyfDA&ixlib=rb-4.1.0&q=85",
    overview:
      "Streaming audience operations needs to know whether a completion drop is a content problem, an audience shift, or a broken viewing experience. AudienceGraph investigates that ambiguity using Gemini on Google Cloud and the official mcp-clickhouse server at application runtime — and returns an evidence-linked decision brief.",
    sections: [
      {
        heading: "How an investigation runs",
        list: [
          "Discovers the ClickHouse schema through the official MCP server",
          "Establishes a baseline for the viewing period",
          "Chooses follow-up cohort tests from observed results",
          "Checks competing explanations — content issue vs audience shift vs playback problem",
          "Returns an evidence-linked decision brief; expanding any evidence card shows the actual MCP SQL and result rows",
          "Exports the full investigation as JSON",
        ],
      },
      {
        heading: "Deterministic guardrails",
        paragraphs: [
          "An explicit arithmetic check prevents a dramatic small cohort from being mistaken for the largest aggregate driver: the audience test decomposes the completion change into audience composition and within-cohort changes. The evidence card shows the calculation, and the export records any headline consistency correction.",
          "Recommendations require human action — the agent cannot change delivery infrastructure. Verification data uses aggregate synthetic data only; no real viewers or copyrighted catalog.",
        ],
      },
      {
        heading: "Modes",
        list: [
          "Run live investigation — real Vertex AI and ClickHouse requests",
          "Explore recorded case — an explicitly labeled replay of a real live run, not a replacement for runtime integration",
        ],
      },
    ],
    stack: ["Gemini", "Vertex AI", "Google Cloud", "ClickHouse", "mcp-clickhouse", "Python", "React", "Cloud Run", "Playwright E2E"],
    links: [
      { label: "Live App", url: "https://audiencegraph-web-kc4mxxrtbq-uc.a.run.app" },
      { label: "GitHub", url: "https://github.com/GameDevKaran/audiencegraph" },
    ],
  },
  "greenlight-ai": {
    name: "Greenlight AI",
    tagline: "Evidence-backed studio decision intelligence",
    role: "Design & Engineering — public repository",
    status: "Live on Cloud Run",
    image:
      "https://images.unsplash.com/photo-1619262105903-c65a6396c72f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBkYXJrJTIwZGFzaGJvYXJkJTIwVUl8ZW58MHx8fHwxNzg5MDgzNDgyfDA&ixlib=rb-4.1.0&q=85",
    overview:
      "Greenlight AI helps producers and development executives assess whether to greenlight a film project. It combines creative material with current market research, separates sourced signals from model interpretation, and produces an auditable decision brief with a concrete action plan. It does not forecast revenue or replace executive judgment. Built for Google Cloud Agentic Cinema: The Blockbuster Hackathon — Parallel partner track.",
    sections: [
      {
        heading: "The seven-stage workflow",
        list: [
          "Project intelligence separates supplied details, inference and unknowns",
          "Research planning chooses 8–18 project-specific questions",
          "Parallel runs searches with concurrency 4, bounded retries and timeouts",
          "Evidence synthesis weighs retained excerpts, contradictions, gaps and relevance",
          "Commercial assessment evaluates all seven dimensions and sourced comparables",
          "Code calculates evidence confidence, weighted score and decision policy",
          "Gemini explains the immutable decision and proposes specific actions",
        ],
      },
      {
        heading: "Runtime architecture",
        paragraphs: [
          "React + TypeScript + Vite → FastAPI → Gemini through Vertex AI (google-genai) → dynamically planned Parallel Search REST requests (httpx) → evidence validation → commercial assessment → deterministic scoring → Gemini executive explanation. SQLite persists reports, evidence and operational progress; the browser polls every 1.5 seconds while running.",
          "Google ADK is deliberately not used: the allowed google-genai stack is used directly to keep orchestration, retry limits and citation governance explicit. Both integrations are essential to the production path — there is no fixture or fake fallback.",
        ],
      },
    ],
    stack: ["React", "TypeScript", "Vite", "FastAPI", "Gemini", "Vertex AI", "google-genai", "Parallel Search", "httpx", "SQLite", "Deterministic Scoring"],
    links: [
      { label: "Live App", url: "https://greenlight-kc4mxxrtbq-uc.a.run.app" },
      { label: "GitHub", url: "https://github.com/GameDevKaran/greenlight-ai-agentic-cinema" },
    ],
  },
};
